'use client'
import type { ColumnDefWithSkeleton } from '@morpho-org/ui-v6/types'

import {
  CellTokenAmountPrice,
  NetworkAvatar as Network,
  type SrcAvatarProps
} from '@morpho-org/ui-v6/client'
import { Skeleton } from '@morpho-org/ui-v6/server'
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui'

import { TimestampCell } from '@/components/common/Cells/TimestampCell'
import UserCellExplorer from '@/components/common/Cells/UserCell/UserCellExplorer'
import { WarningIcon } from '@/components/common/WarningIcon'

import type { LiquidationActivityTableRow } from './DashboardActivity.types'

import { MarketCellWithLink, TransactionHashCell } from '../components'
import {
  type DashboardActivityTableFeatures,
  LIQUIDATION_COLUMN_IDS
} from './DashboardActivity.config'

export const LIQUIDATION_COLUMN_LABELS: Partial<
  Record<keyof typeof LIQUIDATION_COLUMN_IDS, string>
> = {
  // Same order as the columns below, so the column menu reads like the table.
  [LIQUIDATION_COLUMN_IDS.Network]: 'Network',
  [LIQUIDATION_COLUMN_IDS.Timestamp]: 'Date & Time',
  [LIQUIDATION_COLUMN_IDS.Market]: 'Market',
  [LIQUIDATION_COLUMN_IDS.LoanRepaid]: 'Loan Repaid',
  [LIQUIDATION_COLUMN_IDS.CollateralSeized]: 'Collateral Seized',
  [LIQUIDATION_COLUMN_IDS.RealizedBadDebt]: 'Realized Bad Debt',
  [LIQUIDATION_COLUMN_IDS.Liquidator]: 'Liquidator',
  [LIQUIDATION_COLUMN_IDS.TransactionHash]: 'Transaction'
}

const avatarProps: Omit<SrcAvatarProps, 'src'> = {
  size: 'small'
}

export const columnsDefs: ColumnDefWithSkeleton<
  DashboardActivityTableFeatures,
  LiquidationActivityTableRow
>[] = [
  {
    id: LIQUIDATION_COLUMN_IDS.Network,
    accessorFn: row => row.chainId,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">
            {LIQUIDATION_COLUMN_LABELS.Network}
          </span>
        </TooltipTrigger>
        <TooltipContent>The network where the liquidation occurred.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      return (
        <div className="flex items-center">
          <Network chainId={row.original.chainId} srcAvatarProps={avatarProps} />
        </div>
      )
    },
    enableSorting: false,
    skeletonCell: <Skeleton className="h-md w-5 rounded-[20px]" />,

    filterFn: (row, _, filterValue) => filterValue.includes(row.original.chainId)
  },
  {
    id: LIQUIDATION_COLUMN_IDS.Timestamp,
    accessorFn: row => row.date.timestamp,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">
            {LIQUIDATION_COLUMN_LABELS.Timestamp}
          </span>
        </TooltipTrigger>
        <TooltipContent>The date and time the liquidation was executed.</TooltipContent>
      </Tooltip>
    ),
    cell: info => <TimestampCell timestamp={info.row.original.date.timestamp} />,
    enableSorting: true,
    skeletonCell: <Skeleton className="h-md w-[122px] rounded-[20px]" />
  },
  {
    id: LIQUIDATION_COLUMN_IDS.Market,
    header: () => (
      <span className="text-3xs text-secondary-foreground">{LIQUIDATION_COLUMN_LABELS.Market}</span>
    ),
    cell: ({ row }) => <MarketCellWithLink row={row} />,
    enableSorting: false,
    skeletonCell: <Skeleton className="h-md w-[159px] rounded-[20px]" />
  },
  {
    id: LIQUIDATION_COLUMN_IDS.LoanRepaid,
    accessorKey: 'loanRepaid',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">
            {LIQUIDATION_COLUMN_LABELS.LoanRepaid}
          </span>
        </TooltipTrigger>
        <TooltipContent>The amount of debt that was repaid during liquidation.</TooltipContent>
      </Tooltip>
    ),
    cell: info => (
      <CellTokenAmountPrice
        token={info.row.original.loanAsset}
        amount={info.row.original.loanRepaid.amount}
        withAvatar
        value={info.row.original.loanRepaid.amountUsd}
      />
    ),
    enableSorting: false,
    skeletonCell: <Skeleton className="h-md w-[119px] rounded-[20px]" />
  },
  {
    id: LIQUIDATION_COLUMN_IDS.CollateralSeized,
    accessorKey: 'collateralSeized',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">
            {LIQUIDATION_COLUMN_LABELS.CollateralSeized}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          The amount of collateral seized from the liquidated position.
        </TooltipContent>
      </Tooltip>
    ),
    cell: info => (
      <CellTokenAmountPrice
        token={info.row.original.collateralAsset}
        amount={info.row.original.collateralSeized.amount}
        value={info.row.original.collateralSeized.amountUsd}
        withAvatar
      />
    ),
    enableSorting: false,
    skeletonCell: <Skeleton className="h-md w-[119px] rounded-[20px]" />
  },
  {
    id: LIQUIDATION_COLUMN_IDS.RealizedBadDebt,
    accessorKey: 'realizedBadDebt',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">
            {LIQUIDATION_COLUMN_LABELS.RealizedBadDebt}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          The amount of debt that could not be recovered through liquidation.
        </TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <CellTokenAmountPrice
        token={row.original.loanAsset}
        amount={row.original.realizedBadDebt.amount}
        value={row.original.realizedBadDebt.amountUsd}
        renderWarning={
          row.original.realizedBadDebt.warning && (
            <WarningIcon warnings={[row.original.realizedBadDebt.warning]} isSimple />
          )
        }
        withAvatar
      />
    ),
    enableSorting: false,
    skeletonCell: <Skeleton className="h-md w-[119px] rounded-[20px]" />
  },
  {
    id: LIQUIDATION_COLUMN_IDS.Liquidator,
    accessorFn: row => row.liquidator.address,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '7px' }}>
            {LIQUIDATION_COLUMN_LABELS.Liquidator}
          </span>
        </TooltipTrigger>
        <TooltipContent>The address of the liquidator.</TooltipContent>
      </Tooltip>
    ),
    cell: info => (
      <UserCellExplorer
        address={info.row.original.liquidator.address}
        chainId={info.row.original.chainId}
      />
    ),
    enableSorting: false,
    skeletonCell: <Skeleton className="h-md w-[140px] rounded-[20px]" />
  },
  {
    id: LIQUIDATION_COLUMN_IDS.TransactionHash,
    accessorFn: row => row.tx.hash,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '7px' }}>
            {LIQUIDATION_COLUMN_LABELS.TransactionHash}
          </span>
        </TooltipTrigger>
        <TooltipContent>The unique identifier of the transaction.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => <TransactionHashCell row={row} />,
    enableSorting: false,
    skeletonCell: <Skeleton className="h-md w-[119px] rounded-[20px]" />
  }
]
